// ── enemyLogic.js ─────────────────────────────────────────────────────────────
// All logic related to enemy ships: spawning, AI movement/orbiting, firing,
// bullet updates, and death handling (drops, wrecks, echo rolls).
//
// Functions here are called from the HarvestTab canvas loop each frame.
// They mutate the shared `st` game-state object directly for performance —
// this is intentional; React state is only updated for HUD values.
// ─────────────────────────────────────────────────────────────────────────────

import { rollEnemyDrops } from "../data/enemyDrops";
import {
  ARCHETYPE_TURRET, ARCHETYPE_INTERCEPTOR, ARCHETYPE_FRIGATE, ARCHETYPE_GUARDIAN,
  ARCHETYPE_STATS, pickArchetypesForSector,
  pushLaneTelegraph, pushPulseTelegraph, pushSweepTelegraph, pushSafePocketTelegraph,
  pushSliderQte, pushSequenceQte, pushBreachQte,
} from "./combat";

// ── Enemy ship constants ──────────────────────────────────────────────────────

// Base approach speed at sector 0 (px/frame). Scales up with sector depth.
export const ENEMY_SPEED_BASE  = 0.6;
// Frames between enemy shots. Decreases (faster) with sector depth, min 40.
export const ENEMY_FIRE_RATE   = 90;
// Speed of enemy projectiles in px/frame.
export const ENEMY_BULLET_SPEED = 2.8;
// Base damage per enemy bullet. Scales with sector depth.
export const ENEMY_BULLET_DMG  = 8;
// Enemy starts firing when closer than this distance to the player.
export const ENEMY_RANGE       = 180;
// Distance the enemy tries to maintain from the player (orbit behaviour).
export const ENEMY_ORBIT_DIST  = 130;

// ── Wreck constants (shared with playerShip.js / HarvestTab) ─────────────────

// Wrecks drift away and despawn after this many frames if uncollected (~10 s at 60 fps)
export const WRECK_LIFE  = 300;
// How fast a wreck drifts after the ship dies
export const WRECK_DRIFT = 0.18;


// ── Spawning ──────────────────────────────────────────────────────────────────

/**
 * Create a single enemy ship object scaled to the given sector index.
 * All difficulty values (HP, speed, damage, fire rate) grow with depth.
 *
 * @param {number} sectorIdx  - Current sector (0 = first, higher = harder)
 * @param {number} W          - Canvas width  (used to position spawn off-screen)
 * @param {number} H          - Canvas height
 * @returns {object} Enemy ship state object, ready to push into st.enemies
 */
export function spawnEnemy(sectorIdx, W, H, archetype = ARCHETYPE_TURRET) {
  const diff    = 1 + sectorIdx * 0.15;
  const ang     = Math.random() * Math.PI * 2;
  const spawnR  = Math.min(W, H) * 0.46;
  const base    = ARCHETYPE_STATS[archetype];
  const hp       = Math.round(base.hp * diff);
  const speed    = Math.min(base.speed * diff, 2.4);
  const dmg      = Math.round(base.dmg * diff);
  const fireRate = Math.max(40, base.fireRate - sectorIdx * 2);

  return {
    x: W / 2 + Math.cos(ang) * spawnR,
    y: H / 2 + Math.sin(ang) * spawnR,
    vx: 0, vy: 0,
    hp, maxHp: hp,
    speed, dmg, fireRate,
    fireTimer: Math.floor(Math.random() * fireRate),
    angle: ang + Math.PI,
    tier: base.tier,
    archetype,
    size: base.size,
    mobile: base.mobile,
    invulnerable: !!base.invulnerable,
    telegraphTimer: base.telegraphCd + Math.floor(Math.random() * 60),
    telegraphCd:    base.telegraphCd,
    qteTimer: base.qteCd + Math.floor(Math.random() * 120),
    qteCd:    base.qteCd,
    tethering: false,
    armorStripped: 0,
    hitFlash: 0,
    id: Math.random(),
  };
}

/**
 * Populate st.enemies with a fresh wave for the given sector.
 * Enemy count scales with sector depth plus a random ±1 variance.
 *
 * @param {object} st        - Shared game state (mutated in-place)
 * @param {number} sectorIdx
 * @param {number} W
 * @param {number} H
 */
export function initEnemies(st, sectorIdx, W, H, isGateOut = false) {
  st.enemies       = [];
  st.telegraphs    = st.telegraphs ?? [];
  st.qtes          = st.qtes       ?? [];
  st.telegraphs.length = 0;
  st.qtes.length       = 0;

  const archetypes = pickArchetypesForSector(sectorIdx, isGateOut);
  for (const a of archetypes) {
    st.enemies.push(spawnEnemy(sectorIdx, W, H, a));
  }
}


// ── Per-frame update ──────────────────────────────────────────────────────────

/**
 * Update all enemy ships for one frame: movement/orbit AI, firing, and death.
 * Mutates st.enemies, st.enemyBullets, st.wrecks, st.particles, st.floats in place.
 *
 * @param {object} st              - Shared game state
 * @param {number} W               - Canvas width (for boundary clamping)
 * @param {number} H               - Canvas height
 * @param {number} sectorIdx       - Current sector index (for drop scaling)
 * @param {function} addP          - Particle emitter: addP(x, y, color, count)
 * @param {function} addF          - Float text emitter: addF(x, y, text, color)
 * @param {function|null} setMaterials     - React setState for smelted materials (immediate drops)
 * @param {function|null} onItemDrop       - Callback for echo fragment rolls
 * @param {function|null} onDebrisDestroyed - Counter callback (reused for enemy kills)
 */
export function updateEnemies(
  st, W, H, sectorIdx,
  addP, addF,
  setMaterials, onItemDrop, onDebrisDestroyed,
  addXp, xpPerKill = 0, echoMult = 1,
  combatCfg = null
) {
  // combatCfg: { qteSliderWidth, qteFrigateMitigation, qteBreachSlow }
  const cfg = combatCfg ?? { qteSliderWidth: 0.22, qteFrigateMitigation: 0, qteBreachSlow: 0 };
  st.telegraphs = st.telegraphs ?? [];
  st.qtes       = st.qtes       ?? [];

  for (let i = st.enemies.length - 1; i >= 0; i--) {
    const e = st.enemies[i];

    e.hitFlash = Math.max(0, e.hitFlash - 0.07);
    if (e.armorStripped > 0) e.armorStripped--;

    const dx   = st.shipX - e.x;
    const dy   = st.shipY - e.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    e.angle = Math.atan2(dy, dx);

    // ── Movement AI by archetype ───────────────────────────────────────────
    if (e.mobile) {
      if (dist > ENEMY_ORBIT_DIST + 20) {
        e.vx += (dx / dist) * e.speed * 0.08;
        e.vy += (dy / dist) * e.speed * 0.08;
      } else if (dist < ENEMY_ORBIT_DIST - 20) {
        e.vx -= (dx / dist) * e.speed * 0.06;
        e.vy -= (dy / dist) * e.speed * 0.06;
      } else {
        const perp = Math.atan2(dy, dx) + Math.PI / 2;
        e.vx += Math.cos(perp) * e.speed * 0.04;
        e.vy += Math.sin(perp) * e.speed * 0.04;
      }
      const spd = Math.sqrt(e.vx * e.vx + e.vy * e.vy);
      if (spd > e.speed) { e.vx = (e.vx / spd) * e.speed; e.vy = (e.vy / spd) * e.speed; }
      e.vx *= 0.92; e.vy *= 0.92;
      e.x += e.vx; e.y += e.vy;
      e.x = Math.max(20, Math.min(W - 20, e.x));
      e.y = Math.max(20, Math.min(H - 20, e.y));
    }

    // ── Telegraph scheduling (per archetype) ──────────────────────────────
    e.telegraphTimer--;
    if (e.telegraphTimer <= 0 && dist < ENEMY_RANGE * 2.2) {
      e.telegraphTimer = e.telegraphCd;
      if (e.archetype === ARCHETYPE_TURRET)      pushLaneTelegraph(st, e, W, H);
      if (e.archetype === ARCHETYPE_INTERCEPTOR) pushPulseTelegraph(st, e);
      if (e.archetype === ARCHETYPE_FRIGATE)     pushSweepTelegraph(st, e);
      if (e.archetype === ARCHETYPE_GUARDIAN)    pushSafePocketTelegraph(st, e, W, H);
    }

    // ── QTE scheduling (Elites + Boss) ────────────────────────────────────
    if (e.qteCd > 0) {
      e.qteTimer--;
      if (e.qteTimer <= 0) {
        e.qteTimer = e.qteCd;
        if (e.archetype === ARCHETYPE_INTERCEPTOR) pushSliderQte(st, e, cfg.qteSliderWidth);
        if (e.archetype === ARCHETYPE_FRIGATE)     pushSequenceQte(st, e, cfg.qteFrigateMitigation);
        if (e.archetype === ARCHETYPE_GUARDIAN)    pushBreachQte(st, e, cfg.qteBreachSlow);
      }
    }

    // ── Firing (bullets) — turrets still shoot bullets alongside telegraphs ─
    e.fireTimer--;
    if (e.fireTimer <= 0 && dist < ENEMY_RANGE && e.archetype !== ARCHETYPE_INTERCEPTOR) {
      e.fireTimer = e.fireRate;
      const bAng = Math.atan2(st.shipY - e.y, st.shipX - e.x);
      const ba2 = bAng + (Math.random() - 0.5) * 0.08;
      st.enemyBullets.push({
        x: e.x + Math.cos(bAng) * 14,
        y: e.y + Math.sin(bAng) * 14,
        vx: Math.cos(ba2) * ENEMY_BULLET_SPEED,
        vy: Math.sin(ba2) * ENEMY_BULLET_SPEED,
        dmg: e.dmg,
        life: 1,
        decay: 0.004,
      });
      addP(e.x, e.y, "#ff4444", 2);
    }

    // ── Death handling ────────────────────────────────────────────────────────
    if (e.hp <= 0) {
      // Explosion particles
      addP(e.x, e.y, "#ff4422", 18);
      addP(e.x, e.y, "#ffaa33", 8);
      addP(e.x, e.y, "#ffffff", 5);

      // Roll drops for this enemy's tier
      const { drops, materialDrops, echoIds } = rollEnemyDrops(e.tier, sectorIdx);

      // Smelted material drops are credited to the player immediately (no tractor needed)
      if (materialDrops.length > 0 && setMaterials) {
        setMaterials(prev => {
          const upd = { ...prev };
          materialDrops.forEach(m => { upd[m.id] = (upd[m.id] || 0) + m.qty; });
          return upd;
        });
        materialDrops.forEach((m, mi) =>
          addF(e.x, e.y - 20 - mi * 16, `+${m.qty} ${m.name}`, m.color)
        );
      }

      // Echo fragment rolls (raw item IDs; caller handles the rift-bond logic)
      if (onItemDrop) {
        for (const id of echoIds) {
          // Apply node echo multiplier by rolling extra times proportional to the mult
          const rolls = Math.floor(echoMult) + (Math.random() < (echoMult % 1) ? 1 : 0);
          for (let r = 0; r < rolls; r++) onItemDrop(id);
        }
      }

      // Raw salvage goes into a wreck that the player must tractor-collect
      const driftAng = Math.random() * Math.PI * 2;
      st.wrecks.push({
        x: e.x, y: e.y,
        vx: Math.cos(driftAng) * WRECK_DRIFT * (0.5 + Math.random()),
        vy: Math.sin(driftAng) * WRECK_DRIFT * (0.5 + Math.random()),
        size: 14,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.01,
        drops,
        eLoot: 0,
        life: WRECK_LIFE,
        pulseOff: Math.random() * Math.PI * 2,
        color: "#ff5533",
        dropsForFragment: [],  // echoes already rolled above, not re-rolled on collection
        isEnemyWreck: true,
      });

      addF(e.x, e.y, "ENEMY DESTROYED", "#ff6644");
      st.enemies.splice(i, 1);
      if (onDebrisDestroyed) onDebrisDestroyed();
      if (addXp) addXp(25 * e.tier + xpPerKill); // base 25/50/75 per tier + research bonus
    }
  }
}

/**
 * Update enemy bullets for one frame: movement, player collision, expiry.
 * Applies shield absorption before hull damage.
 * Sets isDeadRef and fires onPlayerDeath callback when HP reaches 0.
 *
 * @param {object}   st              - Shared game state
 * @param {number}   W               - Canvas width (OOB check)
 * @param {number}   H               - Canvas height
 * @param {boolean}  dead            - Whether the player is already dead (skip damage)
 * @param {object}   playerHpRef     - Mutable ref holding current player HP
 * @param {object}   isDeadRef       - Mutable ref; set to true on kill
 * @param {number}   SHIELD_RECHARGE_DELAY - Frames before shield starts recharging
 * @param {function} setHp           - Syncs playerHpRef + React state together
 * @param {function} setIsDead       - React setState for death screen
 * @param {function} onPlayerDeath   - Callback (triggers map transition in void.jsx)
 * @param {function} addP            - Particle emitter
 * @param {function} addF            - Float text emitter
 */
export function updateEnemyBullets(
  st, W, H, dead,
  playerHpRef, isDeadRef,
  SHIELD_RECHARGE_DELAY,
  setHp, setIsDead, onPlayerDeath,
  addP, addF
) {
  // Decrement invincibility frames (brief immunity window after being hit)
  if (st.iFrames > 0) st.iFrames--;

  for (let i = st.enemyBullets.length - 1; i >= 0; i--) {
    const b = st.enemyBullets[i];
    b.x += b.vx;
    b.y += b.vy;
    b.life -= b.decay;

    // ── Player collision ──────────────────────────────────────────────────────
    if (!dead && st.iFrames === 0) {
      const dx   = b.x - st.shipX;
      const dy   = b.y - st.shipY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 14) {
        let dmgLeft = b.dmg;

        // Shield absorbs damage first; remainder hits hull
        if (st.shieldHp > 0) {
          const absorbed = Math.min(st.shieldHp, dmgLeft);
          st.shieldHp       = Math.max(0, st.shieldHp - absorbed);
          dmgLeft          -= absorbed;
          st.shieldHitTimer = SHIELD_RECHARGE_DELAY; // reset recharge countdown
          addP(st.shipX, st.shipY, "#4488ff", 6);
          addF(st.shipX, st.shipY - 20, `-${Math.round(absorbed)} SHD`, "#66aaff");
        }

        // Hull damage (whatever shield didn't absorb)
        let newHp = playerHpRef.current;
        if (dmgLeft > 0) {
          newHp = Math.max(0, newHp - dmgLeft);
          addP(st.shipX, st.shipY, "#ff2222", 6);
          addF(
            st.shipX,
            st.shipY - (st.shieldHp > 0 ? 36 : 20),
            `-${Math.round(dmgLeft)} HP`,
            "#ff4444"
          );
        }

        // Sync HP into ref and React state (triggers HUD re-render)
        setHp(newHp, st.shieldHp);
        // Brief invincibility so multi-bullet bursts don't one-shot the player
        st.iFrames = 20;
        st.enemyBullets.splice(i, 1);

        // ── Player death ──────────────────────────────────────────────────────
        if (newHp <= 0 && !isDeadRef.current) {
          isDeadRef.current = true;
          setIsDead(true);
          addP(st.shipX, st.shipY, "#ff2222", 30);
          addP(st.shipX, st.shipY, "#ff8800", 20);
          addP(st.shipX, st.shipY, "#ffffff", 10);
          if (onPlayerDeath) onPlayerDeath();
        }
        continue;
      }
    }

    // Remove bullet if it expired or left the canvas
    if (b.life <= 0 || b.x < 0 || b.x > W || b.y < 0 || b.y > H) {
      st.enemyBullets.splice(i, 1);
    }
  }
}


// ── Drawing ───────────────────────────────────────────────────────────────────

/**
 * Draw all enemy ships and their HP bars onto the canvas context.
 * Called from the draw section of the main loop.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object[]} enemies - st.enemies array
 * @param {number}   pulse   - Shared animation pulse counter (for engine flicker)
 */
export function drawEnemies(ctx, enemies, pulse) {
  for (const e of enemies) {
    ctx.save();
    ctx.translate(e.x, e.y);

    // Coloured glow behind the ship — colour depends on archetype/tier
    const tier   = e.tier ?? 1;
    const isGuardian = e.archetype === ARCHETYPE_GUARDIAN;
    const eColor = isGuardian ? "255,220,80" : tier >= 3 ? "255,60,200" : tier >= 2 ? "255,120,30" : "255,60,60";

    // Invulnerable shimmer (Guardian before breach)
    if (e.invulnerable && !(e.armorStripped > 0)) {
      ctx.strokeStyle = `rgba(200,220,255,${0.4 + Math.sin(pulse * 4) * 0.25})`;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, 0, (e.size ?? 20) + 6, 0, Math.PI * 2); ctx.stroke();
    }
    if (e.armorStripped > 0) {
      ctx.strokeStyle = `rgba(68,255,153,${0.5 + Math.sin(pulse * 5) * 0.3})`;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, 0, (e.size ?? 20) + 6, 0, Math.PI * 2); ctx.stroke();
    }
    const eGlow  = ctx.createRadialGradient(0, 0, 4, 0, 0, 24);
    eGlow.addColorStop(0, `rgba(${eColor},0.22)`);
    eGlow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = eGlow;
    ctx.beginPath(); ctx.arc(0, 0, 24, 0, Math.PI * 2); ctx.fill();

    // Rotate body to face the player
    ctx.rotate(e.angle + Math.PI / 2);
    if (e.hitFlash > 0) ctx.globalAlpha = 0.4 + e.hitFlash * 0.6;

    // Ship body — three visual tiers with increasing detail
    if (tier >= 3) {
      ctx.fillStyle = "#200812"; ctx.strokeStyle = `rgba(${eColor},0.9)`; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(0, -18); ctx.lineTo(-12, 8); ctx.lineTo(-8, 14); ctx.lineTo(8, 14); ctx.lineTo(12, 8); ctx.closePath(); ctx.fill(); ctx.stroke();
      // Wing fins
      ctx.fillStyle = "#1a0810"; ctx.strokeStyle = `rgba(${eColor},0.5)`; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(-10, 4); ctx.lineTo(-20, 12); ctx.lineTo(-10, 11); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo( 10, 4); ctx.lineTo( 20, 12); ctx.lineTo( 10, 11); ctx.closePath(); ctx.fill(); ctx.stroke();
    } else if (tier >= 2) {
      ctx.fillStyle = "#180808"; ctx.strokeStyle = `rgba(${eColor},0.85)`; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(0, -14); ctx.lineTo(-9, 7); ctx.lineTo(-5, 12); ctx.lineTo(5, 12); ctx.lineTo(9, 7); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#140608"; ctx.strokeStyle = `rgba(${eColor},0.4)`; ctx.lineWidth = 0.7;
      ctx.beginPath(); ctx.moveTo(-7, 3); ctx.lineTo(-15, 10); ctx.lineTo(-7, 9); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo( 7, 3); ctx.lineTo( 15, 10); ctx.lineTo( 7, 9); ctx.closePath(); ctx.fill(); ctx.stroke();
    } else {
      // Tier 1: minimal wedge
      ctx.fillStyle = "#180808"; ctx.strokeStyle = `rgba(${eColor},0.8)`; ctx.lineWidth = 1.1;
      ctx.beginPath(); ctx.moveTo(0, -11); ctx.lineTo(-7, 6); ctx.lineTo(7, 6); ctx.closePath(); ctx.fill(); ctx.stroke();
    }

    // Engine glow (flickers with pulse)
    ctx.globalAlpha = 1;
    const engColor = tier >= 3 ? "#ff44cc" : tier >= 2 ? "#ff8833" : "#ff4444";
    ctx.fillStyle = engColor; ctx.shadowColor = engColor; ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.ellipse(0, tier >= 2 ? 14 : 9, 2, 3 + Math.sin(pulse * 3) * 1.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.globalAlpha = 1;
    ctx.restore();

    // HP bar drawn above the ship (outside the rotated save/restore)
    const pct = e.hp / e.maxHp;
    const bw  = 32;
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(e.x - bw / 2, e.y - 28, bw, 3);
    ctx.fillStyle = pct > 0.6 ? "#ff4444" : pct > 0.3 ? "#ff8800" : "#ffcc00";
    ctx.fillRect(e.x - bw / 2, e.y - 28, bw * pct, 3);
  }
}

/**
 * Draw enemy bullets onto the canvas.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object[]} enemyBullets - st.enemyBullets array
 */
export function drawEnemyBullets(ctx, enemyBullets) {
  for (const b of enemyBullets) {
    ctx.globalAlpha = b.life;
    ctx.fillStyle   = "#ff3322";
    ctx.shadowColor = "#ff6644"; ctx.shadowBlur = 5;
    ctx.beginPath(); ctx.arc(b.x, b.y, 3, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;

    // Tail line in the direction the bullet came from
    ctx.strokeStyle = `rgba(255,80,50,${b.life * 0.5})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(b.x - b.vx * 5, b.y - b.vy * 5); ctx.stroke();
    ctx.globalAlpha = 1;
  }
}
